/**
 * Full-screen gate shown when user has accountStatus === 'pending_deletion'.
 * Blocks access until they reactivate (resume subscription) or sign out.
 */

import React, { useState } from 'react';
import { RefreshCw, LogOut } from 'lucide-react';
import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';

export interface ReactivationGateProps {
  tier: string;
  scheduledDeletionAt?: string | null;
  onReactivated?: () => void;
  onLogout?: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

const TIER_LABELS: Record<string, string> = {
  'ai-pro': 'AI Pro',
  'professional': 'Professional',
  'starter': 'Starter',
  'free-trial': 'Free Trial',
};

export const ReactivationGate: React.FC<ReactivationGateProps> = ({
  tier,
  scheduledDeletionAt,
  onReactivated,
  onLogout,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tierLabel = TIER_LABELS[tier?.toLowerCase()] ?? tier ?? 'your plan';
  const deletionDate = scheduledDeletionAt ? formatDate(scheduledDeletionAt) : null;

  const handleReactivate = async () => {
    setError(null);
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.PAYMENTS.RESUME_SUBSCRIPTION);
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('spinora-refetch-user'));
      onReactivated?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to reactivate. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-4 sm:p-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500/20 border border-amber-400/30 mb-2">
          <RefreshCw className="w-4 h-4 mr-2 text-amber-400" />
          <span className="text-amber-200 font-medium">Account scheduled for deletion</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-white">Reactivate your account</h1>
        <p className="text-gray-400">
          You previously requested to delete your account.
          {deletionDate && (
            <> It was scheduled for deletion on {deletionDate}.</>
          )}{' '}
          Reactivate now to keep using {tierLabel} and restore your subscription.
        </p>
        {error && (
          <p className="text-amber-400 text-sm" role="alert">
            {error}
          </p>
        )}
        <button
          onClick={handleReactivate}
          disabled={loading}
          className="w-full py-3 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reactivating…' : 'Reactivate account'}
        </button>
        <div className="flex flex-wrap justify-center gap-3 text-sm">
          <button
            type="button"
            onClick={onLogout}
            className="text-gray-400 hover:text-amber-400 transition-colors flex items-center gap-1"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          <a href="/" className="text-gray-400 hover:text-amber-400 transition-colors">
            Back to home
          </a>
        </div>
      </div>
    </div>
  );
};
